import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  Handle,
  Position,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  BackgroundVariant,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { TableSkeleton } from '../components/ui/Skeletons'

// Icons
const SaveIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
const BackIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
const PlayIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
const PlusIcon = <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface WorkflowData {
  id: string
  name: string
  description: string | null
  definition: string
  trigger_type: string
  status: string
  enabled: number
  version: number
}

interface NodeTypeDef {
  type: string
  category: string
  name: string
  description: string
  icon: string
  color: string
  inputs: { id: string; label?: string; type: string }[]
  outputs: { id: string; label?: string; type: string }[]
  configSchema: unknown[]
}

interface ExecutionRow {
  id: string
  status: string
  started_at: string
  completed_at: string | null
  duration_ms: number | null
  steps_completed: number
  steps_failed: number
  steps_total: number
  error_message: string | null
  step_log: string | null
}

const CATEGORY_COLORS: Record<string, string> = {
  triggers: '#f59e0b',
  logic: '#8b5cf6',
  actions: '#10b981',
  integrations: '#3b82f6',
  data: '#ec4899',
  ai: '#6366f1',
  utilities: '#64748b',
}

// Custom node component
function WorkflowNode({ data }: NodeProps) {
  const color = CATEGORY_COLORS[data.category] || '#64748b'
  const isTrigger = data.nodeType?.startsWith('trigger_')
  const isConditional = data.nodeType === 'logic_if' || data.nodeType === 'logic_switch'

  const handleStyle = {
    width: 10, height: 10, border: '2px solid var(--bg-0)',
    background: color,
  }

  return (
    <div style={{
      background: 'var(--bg-1)',
      border: `2px solid ${color}`,
      borderRadius: 12,
      padding: '10px 14px',
      minWidth: 160,
      boxShadow: '0 2px 8px rgba(0,0,0,.3)',
      position: 'relative',
    }}>
      {/* Input handle — all nodes except triggers */}
      {!isTrigger && (
        <Handle type="target" position={Position.Top} style={handleStyle} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, color,
        }}>
          {data.icon === 'zap' ? '⚡' : data.icon === 'clock' ? '🕐' : data.icon === 'globe' ? '🌐' :
           data.icon === 'play' ? '▶' : data.icon === 'git-branch' ? '🔀' : data.icon === 'bell' ? '🔔' :
           data.icon === 'send' ? '📤' : data.icon === 'camera' ? '📷' : data.icon === 'cpu' ? '🤖' :
           data.icon === 'timer' ? '⏱' : data.icon === 'repeat' ? '🔄' : data.icon === 'filter' ? '🔽' :
           data.icon === 'edit' ? '✏️' : data.icon === 'mail' ? '✉️' : '⚙️'}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>
          {data.label || data.name || data.nodeType}
        </span>
      </div>
      {data.subtitle && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 28, fontFamily: 'var(--font-mono)' }}>
          {data.subtitle}
        </div>
      )}

      {/* Output handles */}
      {isConditional ? (
        <>
          <Handle type="source" position={Position.Bottom} id="true"
            style={{ ...handleStyle, background: '#10b981', left: '30%' }} />
          <Handle type="source" position={Position.Bottom} id="false"
            style={{ ...handleStyle, background: '#ef4444', left: '70%' }} />
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
      )}
    </div>
  )
}

const nodeTypes: NodeTypes = {
  workflowNode: WorkflowNode,
}

export default function WorkflowEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, toastError } = useToast()
  const reactFlowWrapper = useRef<HTMLDivElement>(null)

  const [workflow, setWorkflow] = useState<WorkflowData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [nodeTypeDefs, setNodeTypeDefs] = useState<NodeTypeDef[]>([])
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)
  const [showPalette, setShowPalette] = useState(false)
  const [showExecutions, setShowExecutions] = useState(false)
  const [executions, setExecutions] = useState<ExecutionRow[]>([])
  const [nameEditing, setNameEditing] = useState(false)
  const [tempName, setTempName] = useState('')

  // Load workflow and node types
  useEffect(() => {
    async function load() {
      try {
        const [wfRes, typesRes] = await Promise.all([
          api.get<WorkflowData>(`/workflows/${id}`),
          api.get<{ data: NodeTypeDef[] }>('/workflows/node-types'),
        ])
        setWorkflow(wfRes)
        setNodeTypeDefs(typesRes.data || [])
        setTempName(wfRes.name)

        // Parse definition
        const def = JSON.parse(wfRes.definition)
        const parsedNodes: Node[] = (def.nodes || []).map((n: any) => ({
          id: n.id,
          type: 'workflowNode',
          position: n.position || { x: 0, y: 0 },
          data: {
            ...n.data,
            nodeType: n.type,
            category: findCategory(n.type, typesRes.data || []),
            name: findNodeName(n.type, typesRes.data || []),
            icon: findNodeIcon(n.type, typesRes.data || []),
            label: n.data?.label || n.data?.message_template?.slice(0, 30) || findNodeName(n.type, typesRes.data || []),
            subtitle: n.data?.event_key || n.data?.cron || n.data?.condition || n.data?.action || '',
          },
        }))
        const parsedEdges: Edge[] = (def.edges || []).map((e: any) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          label: e.label,
          animated: e.animated ?? true,
          style: { stroke: 'var(--text-3)', strokeWidth: 2 },
        }))
        setNodes(parsedNodes)
        setEdges(parsedEdges)
      } catch (err) {
        toastError('Failed to load workflow', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, toastError, setNodes, setEdges])

  const onConnect = useCallback((connection: Connection) => {
    setEdges(eds => addEdge({
      ...connection,
      animated: true,
      style: { stroke: 'var(--text-3)', strokeWidth: 2 },
    }, eds))
  }, [setEdges])

  const onNodeClick = useCallback((_: any, node: Node) => {
    setSelectedNode(node)
  }, [])

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  const handleSave = async () => {
    if (!workflow) return
    setSaving(true)
    try {
      // Convert React Flow nodes back to definition format
      const defNodes = nodes.map(n => ({
        id: n.id,
        type: n.data.nodeType || 'trigger_manual',
        position: n.position,
        data: Object.fromEntries(
          Object.entries(n.data).filter(([k]) => !['nodeType', 'category', 'name', 'icon', 'label', 'subtitle'].includes(k))
        ),
        label: n.data.label,
      }))
      const defEdges = edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: e.label,
      }))

      let existingDef: any = {}
      try { existingDef = JSON.parse(workflow.definition) } catch { /* empty */ }

      const definition = JSON.stringify({
        ...existingDef,
        nodes: defNodes,
        edges: defEdges,
      })

      await api.patch(`/workflows/${workflow.id}`, {
        name: tempName || workflow.name,
        definition,
      })
      setWorkflow(prev => prev ? { ...prev, definition, name: tempName || prev.name } : null)
      toast('Workflow saved')
    } catch (err) {
      toastError('Failed to save workflow', err)
    } finally {
      setSaving(false)
    }
  }

  const handleExecute = async () => {
    if (!workflow) return
    try {
      await api.post(`/workflows/${workflow.id}/execute`, {})
      toast('Workflow execution started')
      loadExecutions()
    } catch (err) {
      toastError('Failed to execute', err)
    }
  }

  const loadExecutions = async () => {
    if (!workflow) return
    try {
      const res = await api.get<{ data: ExecutionRow[] }>(`/workflows/${workflow.id}/executions?limit=10`)
      setExecutions(res.data || [])
    } catch { /* silent */ }
  }

  const addNode = useCallback((typeDef: NodeTypeDef) => {
    const newId = `node_${Date.now()}`
    const newNode: Node = {
      id: newId,
      type: 'workflowNode',
      position: { x: 250 + Math.random() * 100, y: 150 + nodes.length * 120 },
      data: {
        nodeType: typeDef.type,
        category: typeDef.category,
        name: typeDef.name,
        icon: typeDef.icon,
        label: typeDef.name,
        subtitle: '',
      },
    }
    setNodes(nds => [...nds, newNode])
    setShowPalette(false)
  }, [nodes.length, setNodes])

  // Group node types by category for palette
  const paletteGroups = useMemo(() => {
    const groups: Record<string, NodeTypeDef[]> = {}
    for (const t of nodeTypeDefs) {
      if (!groups[t.category]) groups[t.category] = []
      groups[t.category].push(t)
    }
    return groups
  }, [nodeTypeDefs])

  if (loading) return <div style={{ padding: 32 }}><TableSkeleton rows={5} /></div>
  if (!workflow) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>Workflow not found</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-0)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button size="sm" variant="ghost" iconOnly icon={BackIcon} onClick={() => navigate('/workflows')} title="Back" />
          {nameEditing ? (
            <input
              autoFocus
              value={tempName}
              onChange={e => setTempName(e.target.value)}
              onBlur={() => setNameEditing(false)}
              onKeyDown={e => { if (e.key === 'Enter') setNameEditing(false) }}
              style={{
                fontSize: 14, fontWeight: 600, background: 'var(--bg-2)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '2px 8px', color: 'var(--text-1)', outline: 'none', width: 240,
              }}
            />
          ) : (
            <span
              onClick={() => setNameEditing(true)}
              style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer', color: 'var(--text-1)' }}
              title="Click to rename"
            >
              {tempName || workflow.name}
            </span>
          )}
          <Badge v={workflow.status === 'active' ? 'ok' : workflow.status === 'error' ? 'err' : 'default'}>
            {workflow.status}
          </Badge>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>v{workflow.version}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => { setShowExecutions(!showExecutions); if (!showExecutions) loadExecutions() }}>
            Runs
          </Button>
          <Button size="sm" variant="secondary" icon={PlayIcon} onClick={handleExecute}>
            Test Run
          </Button>
          <Button size="sm" icon={SaveIcon} onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        <div ref={reactFlowWrapper} style={{ flex: 1, background: 'var(--bg-0)' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            proOptions={{ hideAttribution: true }}
            style={{ background: 'var(--bg-0)' }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="var(--border)" />
            <Controls
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8 }}
            />
            <MiniMap
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8 }}
              nodeColor={() => 'var(--amber)'}
              maskColor="rgba(0,0,0,0.5)"
            />
            <Panel position="top-left">
              <Button
                size="sm"
                variant="secondary"
                icon={PlusIcon}
                onClick={() => setShowPalette(!showPalette)}
              >
                Add Node
              </Button>
            </Panel>
          </ReactFlow>
        </div>

        {/* Node Palette */}
        {showPalette && (
          <div style={{
            position: 'absolute', top: 48, left: 12, zIndex: 10,
            width: 280, maxHeight: 'calc(100% - 64px)', overflowY: 'auto',
            background: 'var(--bg-1)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Add Node</span>
              <button onClick={() => setShowPalette(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16,
              }}>×</button>
            </div>
            {Object.entries(paletteGroups).map(([category, defs]) => (
              <div key={category} style={{ marginBottom: 12 }}>
                <div style={{
                  fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  color: CATEGORY_COLORS[category] || 'var(--text-3)',
                  letterSpacing: '.06em', marginBottom: 6,
                }}>
                  {category}
                </div>
                {defs.map(d => (
                  <button
                    key={d.type}
                    onClick={() => addNode(d)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '6px 8px', marginBottom: 2,
                      background: 'transparent', border: '1px solid transparent',
                      borderRadius: 6, cursor: 'pointer',
                      color: 'var(--text-1)', fontSize: 12, textAlign: 'left',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-2)'; e.currentTarget.style.borderColor = 'var(--border)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent' }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 6,
                      background: `${CATEGORY_COLORS[category]}22`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, flexShrink: 0,
                      color: CATEGORY_COLORS[category],
                    }}>
                      {d.icon === 'zap' ? '⚡' : d.icon === 'clock' ? '🕐' : d.icon === 'globe' ? '🌐' :
                       d.icon === 'play' ? '▶' : d.icon === 'git-branch' ? '🔀' : d.icon === 'bell' ? '🔔' :
                       d.icon === 'send' ? '📤' : d.icon === 'camera' ? '📷' : d.icon === 'cpu' ? '🤖' :
                       d.icon === 'timer' ? '⏱' : d.icon === 'repeat' ? '🔄' : d.icon === 'filter' ? '🔽' :
                       d.icon === 'edit' ? '✏️' : d.icon === 'mail' ? '✉️' : d.icon === 'shuffle' ? '🔀' :
                       d.icon === 'tag' ? '🏷' : d.icon === 'hash' ? '#' : d.icon === 'calendar' ? '📅' :
                       d.icon === 'type' ? 'T' : d.icon === 'layers' ? '📊' : d.icon === 'wifi' ? '📶' :
                       d.icon === 'cloud' ? '☁️' : d.icon === 'radio' ? '📡' : d.icon === 'plus-circle' ? '⊕' :
                       d.icon === 'align-left' ? '≡' : d.icon === 'bar-chart-2' ? '📊' :
                       d.icon === 'list' ? '☰' : d.icon === 'minus' ? '—' : d.icon === 'git-merge' ? '⎇' :
                       d.icon === 'file-text' ? '📄' : d.icon === 'dice' ? '🎲' : '⚙️'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{d.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Config Panel (right side) */}
        {selectedNode && (
          <div style={{
            width: 300, borderLeft: '1px solid var(--border)',
            background: 'var(--bg-1)', overflowY: 'auto', padding: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Node Config</span>
              <button onClick={() => setSelectedNode(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16,
              }}>×</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Type</div>
              <Badge v="accent">{selectedNode.data.nodeType}</Badge>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Label</div>
              <input
                value={selectedNode.data.label || ''}
                onChange={e => {
                  const val = e.target.value
                  setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, label: val } } : n))
                  setSelectedNode(prev => prev ? { ...prev, data: { ...prev.data, label: val } } : null)
                }}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12,
                  background: 'var(--bg-2)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text-1)', outline: 'none',
                }}
              />
            </div>
            {/* Show data fields */}
            {Object.entries(selectedNode.data)
              .filter(([k]) => !['nodeType', 'category', 'name', 'icon', 'label', 'subtitle'].includes(k))
              .map(([key, value]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>{key}</div>
                  <input
                    value={typeof value === 'string' ? value : JSON.stringify(value)}
                    onChange={e => {
                      const val = e.target.value
                      setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, [key]: val } } : n))
                      setSelectedNode(prev => prev ? { ...prev, data: { ...prev.data, [key]: val } } : null)
                    }}
                    style={{
                      width: '100%', padding: '6px 10px', fontSize: 12,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 6, color: 'var(--text-1)', outline: 'none',
                      fontFamily: 'var(--font-mono)',
                    }}
                  />
                </div>
              ))}
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setNodes(nds => nds.filter(n => n.id !== selectedNode.id))
                setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id))
                setSelectedNode(null)
              }}
              style={{ marginTop: 16, width: '100%' }}
            >
              Delete Node
            </Button>
          </div>
        )}

        {/* Execution Panel (right side) */}
        {showExecutions && !selectedNode && (
          <div style={{
            width: 320, borderLeft: '1px solid var(--border)',
            background: 'var(--bg-1)', overflowY: 'auto', padding: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Recent Runs</span>
              <button onClick={() => setShowExecutions(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16,
              }}>×</button>
            </div>
            {executions.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center', padding: 24 }}>No executions yet</div>
            ) : (
              executions.map(ex => (
                <div key={ex.id} style={{
                  padding: '10px 12px', marginBottom: 6,
                  background: 'var(--bg-2)', borderRadius: 8,
                  border: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Badge v={ex.status === 'completed' ? 'ok' : ex.status === 'failed' ? 'err' : ex.status === 'running' ? 'accent' : 'default'} sm>
                      {ex.status}
                    </Badge>
                    <span style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                      {new Date(ex.started_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--text-3)' }}>
                    <span>{ex.steps_completed}/{ex.steps_total} steps</span>
                    {ex.steps_failed > 0 && <span style={{ color: 'var(--err)' }}>{ex.steps_failed} failed</span>}
                    {ex.duration_ms != null && <span>{ex.duration_ms}ms</span>}
                  </div>
                  {ex.error_message && (
                    <div style={{ fontSize: 10, color: 'var(--err)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                      {ex.error_message}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function findCategory(nodeType: string, defs: NodeTypeDef[]): string {
  return defs.find(d => d.type === nodeType)?.category || 'utilities'
}

function findNodeName(nodeType: string, defs: NodeTypeDef[]): string {
  return defs.find(d => d.type === nodeType)?.name || nodeType
}

function findNodeIcon(nodeType: string, defs: NodeTypeDef[]): string {
  return defs.find(d => d.type === nodeType)?.icon || 'settings'
}
